import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api.client'

// ─── Types ────────────────────────────────────────────────────────────────────

interface MasterRecord {
	id: string
	code: string
	label: string
	isActive: boolean
	sortOrder: number
	createdAt: string
	updatedAt: string
}

interface MasterContractType extends MasterRecord {
	contractTypeId: string
}

interface MasterGeoLocation extends MasterRecord {
	geoLocationId: string
	countryCode?: string
}

interface MasterDepartment extends MasterRecord {
	departmentId: string
	parentId: string | null
	children?: MasterDepartment[]
}

interface MasterEnvironment extends MasterRecord {
	environmentId: string
}

interface MasterCloudProvider extends MasterRecord {
	cloudProviderId: string
}

interface MasterLeadershipType extends MasterRecord {
	leadershipTypeId: string
}

type MasterCreateInput = Omit<MasterRecord, 'id' | 'isActive' | 'createdAt' | 'updatedAt'> & {
	countryCode?: string
	parentId?: string | null
}
type MasterUpdateInput = Partial<Omit<MasterRecord, 'id' | 'createdAt' | 'updatedAt'>> & {
	countryCode?: string
	parentId?: string | null
}

// ─── API Functions ────────────────────────────────────────────────────────────

const fetchMasterData = async <T>(endpoint: string): Promise<T[]> => {
	const response = await apiClient.get<T[]>(`/employees/master/${endpoint}`)
	// Handle 204 No Content
	if (response.status === 204 || !response.data) {
		return []
	}
	return response.data
}

const createMasterRecord = async <T>(endpoint: string, data: MasterCreateInput): Promise<T> => {
	const response = await apiClient.post<T>(`/employees/master/${endpoint}`, data)
	return response.data
}

const updateMasterRecord = async <T>(endpoint: string, id: string, data: MasterUpdateInput): Promise<T> => {
	const response = await apiClient.put<T>(`/employees/master/${endpoint}/${id}`, data)
	return response.data
}

const deleteMasterRecord = async (endpoint: string, id: string): Promise<void> => {
	await apiClient.delete(`/employees/master/${endpoint}/${id}`)
}

// ─── Query Options ────────────────────────────────────────────────────────────

// Master data rarely changes, so we can cache it aggressively
const MASTER_DATA_STALE_TIME = 5 * 60 * 1000 // 5 minutes
const MASTER_DATA_CACHE_TIME = 30 * 60 * 1000 // 30 minutes

// ─── Contract Types ───────────────────────────────────────────────────────────

export const useContractTypes = () => {
	return useQuery({
		queryKey: ['contract-types'],
		queryFn: () => fetchMasterData<MasterContractType>('contract-types'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreateContractType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => createMasterRecord<MasterContractType>('contract-types', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['contract-types'] })
		},
	})
}

export const useUpdateContractType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterContractType>('contract-types', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['contract-types'] })
		},
	})
}

export const useDeleteContractType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('contract-types', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['contract-types'] })
		},
	})
}

// ─── Geo Locations ────────────────────────────────────────────────────────────

export const useGeoLocations = () => {
	return useQuery({
		queryKey: ['geo-locations'],
		queryFn: () => fetchMasterData<MasterGeoLocation>('geo-locations'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreateGeoLocation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => createMasterRecord<MasterGeoLocation>('geo-locations', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['geo-locations'] })
		},
	})
}

export const useUpdateGeoLocation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterGeoLocation>('geo-locations', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['geo-locations'] })
		},
	})
}

export const useDeleteGeoLocation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('geo-locations', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['geo-locations'] })
		},
	})
}

// ─── Departments ──────────────────────────────────────────────────────────────

export const useDepartments = () => {
	return useQuery({
		queryKey: ['departments'],
		queryFn: () => fetchMasterData<MasterDepartment>('departments'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreateDepartment = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput & { parentId?: string }) =>
			createMasterRecord<MasterDepartment>('departments', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['departments'] })
		},
	})
}

export const useUpdateDepartment = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterDepartment>('departments', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['departments'] })
		},
	})
}

export const useDeleteDepartment = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('departments', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['departments'] })
		},
	})
}

// ─── Environments ─────────────────────────────────────────────────────────────

export const useEnvironments = () => {
	return useQuery({
		queryKey: ['environments'],
		queryFn: () => fetchMasterData<MasterEnvironment>('environments'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreateEnvironment = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => createMasterRecord<MasterEnvironment>('environments', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['environments'] })
		},
	})
}

export const useUpdateEnvironment = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterEnvironment>('environments', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['environments'] })
		},
	})
}

export const useDeleteEnvironment = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('environments', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['environments'] })
		},
	})
}

// ─── Cloud Providers ──────────────────────────────────────────────────────────

export const useCloudProviders = () => {
	return useQuery({
		queryKey: ['cloud-providers'],
		queryFn: () => fetchMasterData<MasterCloudProvider>('cloud-providers'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreateCloudProvider = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => createMasterRecord<MasterCloudProvider>('cloud-providers', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['cloud-providers'] })
		},
	})
}

export const useUpdateCloudProvider = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterCloudProvider>('cloud-providers', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['cloud-providers'] })
		},
	})
}

export const useDeleteCloudProvider = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('cloud-providers', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['cloud-providers'] })
		},
	})
}

// ─── Leadership Types ────────────────────────────────────────────────────────

export const useLeadershipTypes = () => {
	return useQuery({
		queryKey: ['leadership-types'],
		queryFn: () => fetchMasterData<MasterLeadershipType>('leadership-types'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreateLeadershipType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => createMasterRecord<MasterLeadershipType>('leadership-types', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['leadership-types'] })
		},
	})
}

export const useUpdateLeadershipType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterLeadershipType>('leadership-types', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['leadership-types'] })
		},
	})
}

export const useDeleteLeadershipType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('leadership-types', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['leadership-types'] })
		},
	})
}

// ─── Person Types ────────────────────────────────────────────────────────────

interface MasterPersonType extends MasterRecord {
	personTypeId: string
}

export const usePersonTypes = () => {
	return useQuery({
		queryKey: ['person-types'],
		queryFn: () => fetchMasterData<MasterPersonType>('person-types'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})
}

export const useCreatePersonType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => createMasterRecord<MasterPersonType>('person-types', data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['person-types'] })
		},
	})
}

export const useUpdatePersonType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			updateMasterRecord<MasterPersonType>('person-types', id, data),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['person-types'] })
		},
	})
}

export const useDeletePersonType = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteMasterRecord('person-types', id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['person-types'] })
		},
	})
}

// ─── Type Exports ─────────────────────────────────────────────────────────────

export type {
	MasterCloudProvider,
	MasterContractType,
	MasterCreateInput,
	MasterDepartment,
	MasterEnvironment,
	MasterGeoLocation,
	MasterLeadershipType,
	MasterPersonType,
	MasterRecord,
	MasterUpdateInput,
}
